(() => {
  const init = (root) => {
    if (!root || root.dataset.azReady) return;
    root.dataset.azReady = 'true';

    const track = root.querySelector('[data-az-track]');
    const slides = track ? [...track.children] : [];
    const thumbs = [...root.querySelectorAll('[data-az-thumb]')];
    const dots = [...root.querySelectorAll('.az-pdp__dot')];
    let active = 0;

    const setActive = (index) => {
      active = index;
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    const goTo = (index) => {
      if (!track || !slides[index]) return;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
      setActive(index);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.azThumb))));

    if (track && slides.length > 1) {
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / track.clientWidth);
          if (index !== active) setActive(index);
          ticking = false;
        });
      }, { passive: true });
      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') goTo(Math.min(active + 1, slides.length - 1));
        if (event.key === 'ArrowLeft') goTo(Math.max(active - 1, 0));
      });
    }

    const thumbList = root.querySelector('[data-az-thumbs]');
    root.querySelector('[data-az-thumbs-next]')?.addEventListener('click', () => {
      goTo((active + 1) % slides.length);
      thumbs[active]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (thumbList && active === 0) thumbList.scrollTop = 0;
    });

    // Zoom
    const lightbox = root.querySelector('[data-az-lightbox]');
    const lightboxImg = root.querySelector('[data-az-lightbox-img]');
    root.querySelector('[data-az-zoom]')?.addEventListener('click', () => {
      const img = slides[active]?.querySelector('img');
      if (!img || !lightbox) return;
      const src = img.currentSrc || img.src;
      lightboxImg.src = src.replace(/width=\d+/, 'width=2400');
      lightboxImg.alt = img.alt;
      lightbox.showModal();
    });
    root.querySelector('[data-az-lightbox-close]')?.addEventListener('click', () => lightbox.close());
    lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) lightbox.close(); });

    // Variants
    const variantsEl = root.querySelector('[data-az-variants]');
    const stringsEl = root.querySelector('[data-az-strings]');
    const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    const strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};
    const optionInputs = [...root.querySelectorAll('[data-az-option]')];
    const idInput = root.querySelector('[data-az-variant-id]');
    const now = root.querySelector('[data-az-now]');
    const was = root.querySelector('[data-az-was]');
    const off = root.querySelector('[data-az-off]');
    const add = root.querySelector('[data-az-add]');
    const addLabel = root.querySelector('[data-az-add-label]');

    const picked = () => {
      const groups = {};
      optionInputs.forEach((input) => { if (input.checked) groups[input.dataset.azOption] = input.value; });
      return Object.keys(groups).sort().map((key) => groups[key]);
    };

    const markAvailability = (selection) => {
      optionInputs.forEach((input) => {
        const index = Number(input.dataset.azOption);
        const test = [...selection];
        test[index] = input.value;
        const ok = variants.some((variant) => variant.available && variant.options.every((value, i) => value === test[i]));
        input.classList.toggle('is-unavailable', !ok);
      });
    };

    const update = () => {
      const selection = picked();
      selection.forEach((value, index) => {
        const label = root.querySelector(`[data-az-selected="${index}"]`);
        if (label) label.textContent = value;
      });
      markAvailability(selection);
      const match = variants.find((variant) => variant.options.every((value, i) => value === selection[i]));
      if (!match) {
        add.disabled = true;
        addLabel.textContent = strings.unavailable;
        return;
      }
      idInput.value = match.id;
      now.textContent = match.price;
      was.textContent = match.compare;
      was.hidden = match.pct === 0;
      off.textContent = (strings.off || '').replace('[percent]', match.pct);
      off.hidden = match.pct === 0;
      add.disabled = !match.available;
      add.removeAttribute('aria-disabled');
      addLabel.textContent = match.available ? strings.add : strings.soldOut;
      if (match.media) {
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(match.media));
        if (index > -1) goTo(index);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
    };

    root.addEventListener('change', (event) => { if (event.target.matches('[data-az-option]')) update(); });
    if (optionInputs.length) markAvailability(picked());

    // Share
    const share = root.querySelector('[data-az-share]');
    share?.addEventListener('click', async () => {
      const url = new URL(root.dataset.url, window.location.origin).toString();
      try {
        if (navigator.share) {
          await navigator.share({ title: document.title, url });
          return;
        }
        await navigator.clipboard.writeText(url);
        share.classList.add('is-copied');
        setTimeout(() => share.classList.remove('is-copied'), 2000);
      } catch (error) { /* share dismissed */ }
    });

    // Read more (phones)
    const readMore = root.querySelector("[data-az-readmore]");
    if (readMore) {
      const moreText = readMore.textContent;
      readMore.addEventListener("click", () => {
        const tabsEl = readMore.closest(".az-pdp__tabs");
        const open = tabsEl.classList.toggle("is-expanded");
        readMore.setAttribute("aria-expanded", String(open));
        readMore.textContent = open ? readMore.dataset.less : moreText;
      });
    }

    // Tabs
    const tabs = [...root.querySelectorAll('[data-az-tab]')];
    const selectTab = (tab) => {
      tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectTab(tab));
      tab.addEventListener('keydown', (event) => {
        let next = null;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        if (next) { event.preventDefault(); selectTab(next); next.focus(); }
      });
    });
  };

  const initAll = () => document.querySelectorAll('[data-az-pdp]').forEach(init);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
